import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Divider,
  Chip,
  IconButton,
  List,
} from "@mui/material";
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

export default function CC504({ submitForm, declarationInfo }) {
  const [loading, setLoading] = useState(true);

  // Message fields
  const [messageSender, setMessageSender] = useState("");
  const [messageRecipient, setMessageRecipient] = useState("");

  // Answer fields
  const [answerScope, setAnswerScope] = useState("Declaration");
  const [justification, setJustification] = useState("");

  // Additional documents
  const [additionalDocuments, setAdditionalDocuments] = useState([]);

  useEffect(() => {
    if (declarationInfo) {
      // Map data from CC415 declaration
      if (declarationInfo.declaration_info) {
        setMessageSender(declarationInfo.declaration_info.message_sender || "");
        setMessageRecipient(
          declarationInfo.declaration_info.message_recipient || ""
        );
      }
    }

    setLoading(false);
  }, [declarationInfo]);

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Content = e.target.result.split(",")[1]; // Remove data:*/*;base64, prefix

        const newDocument = {
          document_reference: `REF-${Date.now()}-${
            additionalDocuments.length + 1
          }`,
          description: `${file.name} document`,
          file_type: file.type || "application/octet-stream",
          file_name: file.name,
          file_content: base64Content,
        };

        setAdditionalDocuments((prev) => [...prev, newDocument]);
      };
      reader.readAsDataURL(file);
    });

    // Reset file input
    event.target.value = "";
  };

  const handleDeleteDocument = (index) => {
    setAdditionalDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDocumentFieldChange = (index, field, value) => {
    setAdditionalDocuments((prev) =>
      prev.map((doc, i) => (i === index ? { ...doc, [field]: value } : doc))
    );
  };

  const handleSubmit = () => {
    const requestData = {
      answer_scope: answerScope,
      justification: justification,
      additional_documents:
        additionalDocuments.length > 0 ? additionalDocuments : undefined,
    };

    submitForm({ messageType: "CC504", data: requestData });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div style={{ margin: "20px 0" }}>
      <Paper
        sx={{ boxShadow: "none" }}
        style={{
          padding: "20px",
          marginBottom: "20px",
          backgroundColor: "#f5f5f5",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Declaration Information
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Message Sender"
              value={messageSender}
              onChange={(e) => setMessageSender(e.target.value)}
              fullWidth
              required
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Message Recipient"
              value={messageRecipient}
              onChange={(e) => setMessageRecipient(e.target.value)}
              fullWidth
              required
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              MRN
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" style={{ fontWeight: 500 }}>
                {declarationInfo?.mrn || "N/A"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Message Type
            </Typography>
            <Chip label="CC504" color="primary" />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Preparation Date & Time
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Will be automatically set to current moment when submitting
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          CC504 - Raspuns discrepante majore
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Answer Scope</InputLabel>
              <Select
                value={answerScope}
                label="Answer Scope"
                onChange={(e) => setAnswerScope(e.target.value)}
              >
                <MenuItem value="Declaration">Declaration</MenuItem>
                <MenuItem value="Amendment request">Amendment request</MenuItem>
                <MenuItem value="Invalidation request">
                  Invalidation request
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Justification"
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              fullWidth
              required
              multiline
              rows={4}
              placeholder="Enter justification for major discrepancies response..."
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          Additional Documents (Optional)
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />

        <Box mb={2}>
          <input
            accept="*/*"
            style={{ display: "none" }}
            id="additional-documents-upload"
            type="file"
            multiple
            onChange={handleFileUpload}
          />
          <label htmlFor="additional-documents-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<AttachFileIcon />}
            >
              Upload Documents
            </Button>
          </label>
          <Typography
            variant="caption"
            display="block"
            mt={1}
            color="textSecondary"
          >
            Files will be automatically converted to base64 format for XML
          </Typography>
        </Box>

        {additionalDocuments.length > 0 && (
          <List>
            {additionalDocuments.map((doc, index) => (
              <Paper
                key={index}
                style={{ padding: "15px", marginBottom: "10px" }}
                elevation={1}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Document Reference"
                      value={doc.document_reference}
                      onChange={(e) =>
                        handleDocumentFieldChange(
                          index,
                          "document_reference",
                          e.target.value
                        )
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="File Name"
                      value={doc.file_name}
                      onChange={(e) =>
                        handleDocumentFieldChange(
                          index,
                          "file_name",
                          e.target.value
                        )
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      label="Description"
                      value={doc.description}
                      onChange={(e) =>
                        handleDocumentFieldChange(
                          index,
                          "description",
                          e.target.value
                        )
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="File Type"
                      value={doc.file_type}
                      onChange={(e) =>
                        handleDocumentFieldChange(
                          index,
                          "file_type",
                          e.target.value
                        )
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="caption" color="textSecondary">
                        Base64 content size:{" "}
                        {((doc.file_content.length * 0.75) / 1024).toFixed(2)}{" "}
                        KB
                      </Typography>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteDocument(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </List>
        )}
      </Paper>

      <Box display="flex" gap={2} justifyContent="center" mt={3}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SendIcon />}
          onClick={handleSubmit}
        >
          Trimite CC504
        </Button>
      </Box>
    </div>
  );
}
